import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, Clock, MessageSquarePlus, FileText, Presentation, Loader2 } from 'lucide-react';
import { STATUS_META } from '@/constants/projectMeta';

const Stat = ({ label, value, color }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2.5">
    <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">{label}</p>
    <p className="text-xl font-semibold mt-0.5" style={{ color }}>{value}</p>
  </div>
);

export const ProjectWeeklyReview = ({ data, week, onWeekChange, loading, onAddUpdate, projectLabel, exporting, onExport }) => {
  if (!data) return <div className="py-16 text-center text-sm text-[hsl(var(--text-muted))]">Loading…</div>;
  const s = data.stats || {};

  return (
    <div className="space-y-4" data-testid="weekly-review">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onWeekChange(-1)} data-testid="week-prev"><ChevronLeft size={14} /></Button>
          <div className="text-sm font-semibold" data-testid="week-label">
            {week} <span className="text-[hsl(var(--text-muted))] font-normal">({data.range?.from} → {data.range?.to})</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => onWeekChange(1)} data-testid="week-next"><ChevronRight size={14} /></Button>
          {projectLabel && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" data-testid="weekly-project-label">
              {projectLabel}
            </span>
          )}
          {loading && <span className="text-xs text-[hsl(var(--text-muted))]">Loading…</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onExport('pdf')} disabled={!!exporting} data-testid="weekly-export-pdf">
            {exporting === 'pdf' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5" />}
            Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => onExport('pptx')} disabled={!!exporting} data-testid="weekly-export-pptx">
            {exporting === 'pptx' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Presentation size={14} className="mr-1.5" />}
            Export PowerPoint
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        <Stat label="Total Tasks" value={s.total || 0} color="hsl(var(--text-primary))" />
        <Stat label="Updated" value={s.reviewed || 0} color="#10b981" />
        <Stat label="No Update" value={s.missing || 0} color="#f59e0b" />
        <Stat label="Issues" value={s.issues || 0} color="#ef4444" />
        <Stat label="Overdue" value={s.overdue || 0} color="#ef4444" />
      </div>

      {!!(data.issues || []).length && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3" data-testid="weekly-issues">
          <p className="text-xs font-semibold text-red-400 flex items-center gap-1.5 mb-2">
            <AlertTriangle size={13} /> Issues & blockers this week
          </p>
          <div className="space-y-1.5">
            {data.issues.map((i, idx) => (
              <div key={`${i.task_id}-${idx}`} className="text-xs" data-testid={`weekly-issue-${idx}`}>
                <span className="font-medium text-[hsl(var(--text-primary))]">{i.title}</span>
                <span className="text-[hsl(var(--text-muted))]"> · {i.pic_name || '—'} · </span>
                <span className="text-red-300">{i.issue}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-2.5 text-emerald-400">
            <CheckCircle2 size={13} /> Updated ({(data.reviewed || []).length})
          </p>
          <div className="space-y-2" data-testid="weekly-reviewed">
            {(data.reviewed || []).map((t) => (
              <div key={t.id} className="rounded-lg border border-[hsl(var(--border))] p-2.5" data-testid={`weekly-reviewed-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-medium text-[hsl(var(--text-primary))]">{t.title}</p>
                  <Badge className={`${(STATUS_META[t.status] || STATUS_META.pending).bg} ${(STATUS_META[t.status] || STATUS_META.pending).text} text-[10px]`}>
                    {(STATUS_META[t.status] || STATUS_META.pending).label}
                  </Badge>
                </div>
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">{t.department} · {t.pic_name || '—'} · {t.progress}%</p>
                {t.updates.map((u) => (
                  <div key={u.id} className="mt-1.5 pl-2 border-l border-[hsl(var(--border))]">
                    {u.update && <p className="text-[11px] text-[hsl(var(--text-primary))]">{u.update}</p>}
                    {u.issue && <p className="text-[11px] text-red-400">Issue: {u.issue}</p>}
                    {u.next_step && <p className="text-[10px] text-[hsl(var(--text-muted))]">Next: {u.next_step}</p>}
                  </div>
                ))}
              </div>
            ))}
            {!(data.reviewed || []).length && <p className="text-xs text-[hsl(var(--text-muted))]">No updates this week.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
          <p className="text-xs font-semibold flex items-center gap-1.5 mb-2.5 text-amber-400">
            <Clock size={13} /> Awaiting update ({(data.missing || []).length})
          </p>
          <div className="space-y-1.5" data-testid="weekly-missing">
            {(data.missing || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--border))] p-2" data-testid={`weekly-missing-${t.id}`}>
                <div>
                  <p className="text-xs text-[hsl(var(--text-primary))]">{t.title}</p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))]">{t.department} · {t.pic_name || '—'} · {t.progress}%</p>
                </div>
                <button onClick={() => onAddUpdate(t.id)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] shrink-0"
                  data-testid={`weekly-add-update-${t.id}`}>
                  <MessageSquarePlus size={14} />
                </button>
              </div>
            ))}
            {!(data.missing || []).length && <p className="text-xs text-[hsl(var(--text-muted))]">All tasks have an update. Nice.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
